<script lang="ts">
	/**
	 * ExhibitDenialPattern — three CO-197 denials clustering into one
	 * drafted appeal + recovery line.
	 *
	 * Beat 05: Denials, recovered.
	 * Pain anchor: 60% YoY denial-rate increase (MGMA Stat 2024);
	 *              11.8% initial denial rate (AAPC 2024).
	 *
	 * Visual: three rows with the same payer + denial code (UHC CO-197),
	 * a hairline arrow down to one drafted appeal block, then a recovery
	 * line in sage at the bottom.
	 */

	import EkaviMark from '../EkaviMark.svelte';

	const DENIALS = [
		{ patient: 'Hernandez', date: 'May 1', amount: '$1,420' },
		{ patient: 'Wong', date: 'May 3', amount: '$1,380' },
		{ patient: 'Singh', date: 'May 4', amount: '$1,400' }
	] as const;
</script>

<figure
	class="exhibit"
	aria-label="Three denials with the same payer and code clustering into one drafted appeal"
>
	<div class="head">
		<span class="eyebrow">Pattern detected · UHC CO-197</span>
		<span class="freq">3× this week</span>
	</div>

	<ul class="denials">
		{#each DENIALS as d (d.patient)}
			<li class="denial">
				<span class="dot" aria-hidden="true"></span>
				<span class="patient">{d.patient}</span>
				<span class="meta">UHC · CO-197 · MRI lumbar</span>
				<span class="date">{d.date}</span>
				<span class="amount">{d.amount}</span>
			</li>
		{/each}
	</ul>

	<div class="arrow" aria-hidden="true">
		<span class="arrow-line"></span>
		<span class="arrow-label">drafted as one appeal</span>
		<span class="arrow-line"></span>
	</div>

	<div class="appeal">
		<span class="appeal-mark" aria-hidden="true">
			<EkaviMark size={14} decorative maskIdSuffix="dp-appeal" />
		</span>
		<div class="appeal-body">
			<p class="appeal-title">Appeal — UHC · CO-197 · 3 cases</p>
			<p class="appeal-meta">
				Cited: PA reference UHC-PA-9912034 · policy revised April 2026 · prior PT documented (six
				weeks)
			</p>
		</div>
	</div>

</figure>

<style>
	.exhibit {
		background: var(--ekavi-paper);
		border: var(--ekavi-stroke-hairline);
		padding: var(--ekavi-space-6);
		margin: 0;
	}
	.head {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		padding-bottom: var(--ekavi-space-3);
		border-bottom: var(--ekavi-stroke-hairline);
	}
	.eyebrow {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
	}
	.freq {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ochre);
		font-variant-numeric: tabular-nums;
		font-weight: var(--ekavi-weight-halbfett);
	}

	.denials {
		list-style: none;
		padding: 0;
		margin: 0;
	}
	.denial {
		display: grid;
		grid-template-columns: 16px minmax(80px, 1fr) minmax(0, 2fr) auto auto;
		align-items: center;
		gap: var(--ekavi-space-3);
		padding-block: var(--ekavi-space-2);
		border-bottom: var(--ekavi-stroke-hairline);
	}
	.denial:last-child {
		border-bottom: none;
	}
	.dot {
		width: 6px;
		height: 6px;
		border-radius: 999px;
		background: var(--ekavi-ochre);
		display: inline-block;
		margin-inline: auto;
	}
	.patient {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-halbfett);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-1);
	}
	.meta {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-2);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.date {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		font-variant-numeric: tabular-nums;
	}
	.amount {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-2);
		font-variant-numeric: tabular-nums;
		text-align: right;
		min-width: 56px;
	}

	.arrow {
		display: flex;
		align-items: center;
		gap: var(--ekavi-space-3);
		padding-block: var(--ekavi-space-3);
	}
	.arrow-line {
		flex: 1;
		height: 1px;
		background: var(--ekavi-hairline);
	}
	.arrow-label {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
	}

	.appeal {
		display: grid;
		grid-template-columns: 24px 1fr;
		gap: var(--ekavi-space-3);
		padding: var(--ekavi-space-3);
		border: var(--ekavi-stroke-hairline);
		background: var(--ekavi-bone);
		align-items: start;
	}
	.appeal-mark {
		display: flex;
		justify-content: center;
		padding-top: 4px;
		color: var(--ekavi-charcoal);
	}
	.appeal-title {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-1);
	}
	.appeal-meta {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		margin: 0;
		line-height: 1.55;
	}


	@media (max-width: 480px) {
		.denial {
			grid-template-columns: 16px 1fr auto auto;
		}
		.meta {
			grid-column: 2 / -1;
			grid-row: 2;
			margin-top: 2px;
		}
	}
</style>
